import React, { useState } from 'react';
import { Box, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { AuthPermissions } from 'skipify-types';

import PageWrapper from '../../common/PageWrapper';
import {
  CreateMerchantForm,
  CreateMerchantFormValues,
  resolveCreateMerchantIndustry,
} from './components/CreateMerchantForm';
import { useCreateMerchantMutation } from '../../store/modules/merchant/api';
import { useMerchant } from '../../contexts/MerchantContext';
import { CreateMerchantBody, CreateMerchantResponse } from '../../store/modules/merchant/types';
import { toast } from '../../util/toast';
import {
  buildEcommerceConnectorData,
  normalizeEcommerceExternalId,
} from '../../util/ecommerceConnector';
import { MerchantCreatedDialog } from '../../common/MerchantCreate';

const buildCreateMerchantBody = (formValues: CreateMerchantFormValues): CreateMerchantBody => {
  const credentials: NonNullable<CreateMerchantBody['credentials']> = [];
  const urls: NonNullable<CreateMerchantBody['urls']> = [];
  let externalPlatformId: string | undefined;

  if (formValues.domain) {
    urls.push({ backendUrl: formValues.domain, url: formValues.domain });
  }

  if (formValues.ecommercePlatform !== 'custom') {
    // Validation has already rejected anything the normalizer would return null for.
    externalPlatformId = normalizeEcommerceExternalId(
      formValues.ecommercePlatform,
      formValues.externalId,
    )!;

    credentials.push({
      credentialType: 'ecommerce',
      systemOfOrigin: formValues.ecommercePlatform,
      data: buildEcommerceConnectorData(formValues.ecommercePlatform, externalPlatformId),
    });
  }

  if (formValues.paymentGateway !== 'none') {
    if (!formValues.paymentGatewayData) {
      throw new Error('Payment gateway data is required');
    }
    credentials.push({
      credentialType: 'payment',
      systemOfOrigin: formValues.paymentGateway,
      data: formValues.paymentGatewayData,
    });
  }

  return {
    name: formValues.name,
    industry: resolveCreateMerchantIndustry(formValues),
    ...(formValues.supportEmail.trim() && { supportEmail: formValues.supportEmail.trim() }),
    ...(externalPlatformId && { externalPlatformId }),
    ...(credentials.length > 0 && { credentials }),
    ...(urls.length > 0 && { urls }),
  };
};

export const CreateMerchant = () => {
  const { refreshMerchantAccess, canAccess } = useMerchant();
  const hasAdminAccess = canAccess(AuthPermissions.ADMIN_ALL);
  const [createMerchant, { isLoading: isSubmitting }] = useCreateMerchantMutation();
  const [createdMerchant, setCreatedMerchant] = useState<CreateMerchantResponse | null>(null);

  return (
    <PageWrapper title="Create Merchant" description="Create a new top-level merchant account">
      <Stack
        spacing={4}
        sx={{
          maxWidth: { xs: '100%', md: '500px', lg: '800px' },
        }}
      >
        <Box
          sx={{
            '& p, span': {
              color: 'common.black',
              fontSize: '14px',
            },
            '& b': {
              fontWeight: 600,
            },
          }}
        >
          <Typography>
            You&apos;re about to create a top-level merchant account. Please note the following
            before proceeding:
          </Typography>
          <List
            sx={{
              listStyleType: 'disc',
              color: 'common.black',
              pl: 2,
              py: 2,
              '& li': {
                display: 'list-item',
                pl: 1,
                py: 0.25,
              },
            }}
          >
            <ListItem>
              <ListItemText>
                This account has <b>no parent</b>. It sits at the top of its own hierarchy.
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                Ecommerce platform credentials only record the store identity. Leave the platform as{' '}
                <b>Custom</b> to omit them and let Skipify apply its defaults.
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemText>
                A payment gateway is optional. Pick one only when you have its credentials to hand.
              </ListItemText>
            </ListItem>
          </List>
        </Box>
        <CreateMerchantForm
          isSubmitting={isSubmitting}
          readOnly={!hasAdminAccess}
          onSubmit={async (formValues) => {
            try {
              const requestBody = buildCreateMerchantBody(formValues);
              const result = await createMerchant(requestBody).unwrap();

              if (!result.apiKeys?.[0]?.apiKey) {
                toast.info(
                  'Merchant was created, but no API key was returned. Contact support before using it.',
                );
              }

              try {
                await refreshMerchantAccess();
              } catch {
                toast.error(
                  'Merchant created, but refreshing access failed. You can still copy the IDs below.',
                );
              }

              setCreatedMerchant(result);
            } catch (error) {
              toast.error(
                'Something went wrong while creating the merchant. Please try again later.',
              );
              throw error;
            }
          }}
        />
      </Stack>
      {createdMerchant && (
        <MerchantCreatedDialog
          createdMerchant={createdMerchant}
          open={!!createdMerchant}
          onClose={() => setCreatedMerchant(null)}
        />
      )}
    </PageWrapper>
  );
};
